import React, { useEffect, useState } from "react";
import styles from "./DurationCrypto.module.scss";
import { AssetIcon, CatalogItem } from "../../../pages/Pools/catalogFacade";

const iconPaths: Partial<Record<AssetIcon, string>> = {
  eth: "/icons/eth.svg",
  sol: "/icons/sol.svg",
  usdc: "/icons/usdc.svg",
  btc: "/icons/btc.svg",
  ton: "/icons/ton.svg",
  pol: "/icons/matic.svg",
  bnb: "/icons/bnb.svg",
  usdt: "/icons/usdt.svg",
  atom: "/icons/Cosmos (ATOM).svg",
  dot: "/icons/dot.svg",
  near: "/icons/NEAR Protocol (NEAR).svg",
  sui: "/icons/sui.png",
  apt: "/icons/apt.png",
  ada: "/icons/ada.svg",
  avax: "/icons/Avalanche (AVAX).svg",
  tia: "/icons/tia.png",
  sei: "/icons/sei.png",
};

const AssetIcons = ({ item }: { item: CatalogItem }) => (
  <div className={styles.assetIcons} aria-label={`${item.symbol} assets`}>
    {item.logo ? <img src={item.logo} alt="" onError={(event) => {
      event.currentTarget.src = "/icons/default.svg";
    }} /> : item.icons.map((icon, index) =>
      iconPaths[icon] ? (
        <img key={`${icon}-${index}`} src={iconPaths[icon]} alt="" />
      ) : <img
        key={`${icon}-${index}`}
        src={`/icons/${encodeURIComponent(icon)}.svg`}
        alt=""
        onError={(event) => {
          if (!event.currentTarget.src.endsWith("/icons/default.svg")) {
            event.currentTarget.src = "/icons/default.svg";
          }
        }}
      />
    )}
  </div>
);

const rateHeading: Record<CatalogItem["category"], string> = {
  Staking: "Product data",
  Lending: "Supply APY",
  "Credit pools": "Supply APY",
  Spot: "Market price",
  Derivatives: "Mark price",
  "Tokenized assets": "Market price",
};

const RateValue = ({ value }: { value: string }) => {
  const match = value.match(/^([\d,.]+%)(?:\s+(.+))?$/i);
  if (!match) return <span className={styles.rateNumber}>{value}</span>;
  return (
    <span className={styles.rateValue}>
      <span className={styles.rateNumber}>{match[1]}</span>
      {match[2] && <span className={styles.rateLabel}>{match[2]}</span>}
    </span>
  );
};

const BestRateBadge = () => <span className={styles.bestRateBadge}><i />Best rate</span>;

export const ProviderRows = ({ items, category }: { items: CatalogItem[]; category: CatalogItem["category"] }) => {
  const [showAll, setShowAll] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [selectedItem, setSelectedItem] = useState<CatalogItem | null>(null);
  const [selectedOfferIndex, setSelectedOfferIndex] = useState(0);

  useEffect(() => {
    if (!showAll && !selectedItem) return;
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [showAll, selectedItem]);
  const desktopRows = items.slice(0, 10);
  const mobileRows = items.slice(0, 5);
  const catalogRows = items.filter((item) =>
    `${item.asset} ${item.symbol} ${item.provider} ${item.network}`
      .toLowerCase()
      .includes(catalogSearch.toLowerCase())
  );
  const openProduct = (item: CatalogItem) => {
    setSelectedOfferIndex(0);
    setSelectedItem(item);
  };
  const selectedOffer = selectedItem?.offers?.[selectedOfferIndex] || selectedItem?.offers?.[0];
  const optionKind = selectedItem?.source === "Jito Restaking" ? "vault" :
    selectedItem?.category === "Staking" && selectedItem?.source === "Hyperliquid" ? "validator" : "provider";
  const actionLabel = selectedItem?.category === "Lending" || selectedItem?.category === "Credit pools" ? "Supply" :
    selectedItem?.category === "Staking" ? "Stake" : "Trade";
  const metricLabel = selectedItem?.source === "Jito Restaking" ? "Vault exchange rate" :
    selectedItem?.category === "Lending" || selectedItem?.category === "Credit pools" ? "Supply APY" :
    selectedItem?.category === "Staking" ? "Estimated APR" : "Market price";
  const comparableOffers = Boolean(selectedItem?.offers?.length && selectedItem.offers.length > 1 &&
    selectedItem.offers.every((offer) => offer.rate_percent !== undefined));

  return (
    <div className={styles.durations__bodyWrapper}>
      <div className={styles.durations__body}>
        <div className={styles.durations__bodyHeaderLine}>
          <div className={styles.durations__bodyHeaderCoin}>Asset</div>
          <div className={styles.durations__bodyHeaderArp}>{rateHeading[category]}</div>
          <div className={styles.durations__bodyHeaderProvider}>Provider</div>
          <div className={styles.durations__bodyHeaderDuration}>Terms</div>
          <div />
        </div>
        {desktopRows.map((item) => (
          <div key={item.id} className={styles.durations__bodyLine}>
            <div className={styles.durations__bodyLineCoinWrapper}>
              <div className={styles.durations__bodyLineCoinImg}><AssetIcons item={item} /></div>
              <div className={styles.assetCopy}>
                <div className={styles.durations__bodyLineCoin}>{item.asset}</div>
                <div className={styles.assetSymbol}>{item.symbol}</div>
              </div>
            </div>
            <div className={styles.durations__bodyLineProcentWrapper}>
              <div className={styles.durations__bodyLineProcent}><RateValue value={item.rate} /></div>
              {item.offers && item.offers.length > 1 &&
                item.offers.every((offer) => offer.rate_percent !== undefined) &&
                item.offers[0].rate === item.rate && <BestRateBadge />}
            </div>
            <div className={styles.durations__bodyLineDurationWrapper}>
              <div className={styles.providerIdentity}><strong>{item.provider}</strong><span>{item.network}</span></div>
            </div>
            <div className={styles.durations__bodyLineDurationWrapper}>
              <span className={styles.termChip}>{item.lock}</span>
            </div>
            <div className={styles.durations__bodyLineButtonBack}>
              <button className={styles.durations__bodyLineButton} onClick={() => openProduct(item)}>
                View details
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.durations__bodyMobil}>
        <div className={styles.durations__bodyHeadMobil}>
          <div className={styles.durations__headCoinMobil}>Asset</div>
          <div className={styles.durations__headAPRMobil}>{rateHeading[category]}</div>
        </div>
        {mobileRows.map((item) => (
          <div className={styles.durations__bodyBoxMobil} key={`${item.id}-mobile`}>
            <div className={styles.durations__BoxNameWrapperMobil}>
              <div className={styles.durations__BoxNameMobil}>
                <div className={styles.durations__BoxNameMobilImg}><AssetIcons item={item} /></div>
                <div>
                  <div>{item.asset}</div>
                  <div className={styles.assetSymbol}>{item.symbol}</div>
                </div>
              </div>
              <div className={styles.durations__BoxValueMobil}>
                <div className={styles.durations__BoxValueProcentMobil}><RateValue value={item.rate} /></div>
              </div>
            </div>
            <div className={styles.durations__BoxNameDurationMobil}>Provider</div>
            <div className={styles.durations__BoxDurationWrapperMobil}>
              <div className={styles.providerTerms}>
                <span className={styles.providerName}>{item.provider}</span>
                <span className={styles.providerNetwork}>{item.network}</span>
              </div>
            </div>
            <div className={styles.mobileTerms}><span>Terms</span><strong>{item.lock}</strong></div>
            <button className={styles.durations__mobileBuyButton} onClick={() => openProduct(item)}>
              View details
            </button>
          </div>
        ))}
      </div>

      {items.length > 5 && (
        <button className={styles.durations__showMoreBtn} onClick={() => setShowAll(true)}>
          View all
        </button>
      )}
      {showAll && (
        <div className={styles.catalogOverlay} role="presentation" onMouseDown={() => setShowAll(false)}>
          <section className={styles.catalogDialog} role="dialog" aria-modal="true" aria-label="All assets" onMouseDown={(event) => event.stopPropagation()}>
            <div className={styles.catalogDialogHeader}>
              <div><strong>All assets</strong><span>{items.length} products</span></div>
              <button type="button" onClick={() => setShowAll(false)} aria-label="Close asset catalog"><span /></button>
            </div>
            <input
              className={styles.catalogSearch}
              value={catalogSearch}
              onChange={(event) => setCatalogSearch(event.target.value)}
              placeholder="Search asset, symbol, provider or network"
              autoFocus
            />
            <div className={styles.catalogList}>
              {catalogRows.map((item) => (
                <button
                  type="button"
                  key={`${item.id}-catalog`}
                  className={styles.catalogItem}
                  onClick={() => {
                    setSelectedOfferIndex(0);
                    setShowAll(false);
                    setSelectedItem(item);
                  }}
                  aria-label={`View ${item.asset} details`}
                >
                  <AssetIcons item={item} />
                  <div className={styles.catalogItemName}><strong>{item.asset}</strong><span>{item.symbol} · {item.provider}</span></div>
                  <div className={styles.catalogItemMeta}><strong>{item.rate}</strong><span>{item.network}</span></div>
                </button>
              ))}
              {catalogRows.length === 0 && <div className={styles.catalogEmpty}>No matching assets.</div>}
            </div>
          </section>
        </div>
      )}
      {selectedItem && (
        <div className={`${styles.catalogOverlay} ${styles.productOverlay}`} role="presentation" onMouseDown={() => setSelectedItem(null)}>
          <section className={styles.productDrawer} role="dialog" aria-modal="true" aria-label={`${selectedItem.asset} product details`} onMouseDown={(event) => event.stopPropagation()}>
            <div className={styles.productHeader}>
              <div className={styles.productIdentity}>
                <AssetIcons item={selectedItem} />
                <div><strong>{selectedItem.asset}</strong><span>{selectedItem.symbol} · {selectedItem.category}</span></div>
              </div>
              <button type="button" onClick={() => setSelectedItem(null)} aria-label="Close product details"><span /></button>
            </div>
            <div className={styles.productScroll}>
              <div className={styles.productSummary}>
                <div><span>{metricLabel}</span><strong>{selectedOffer?.rate || selectedItem.rate}</strong></div>
                <div><span>Network</span><strong>{selectedOffer?.network || selectedItem.network}</strong></div>
                <div><span>Exit terms</span><strong>{selectedOffer?.terms || selectedItem.lock}</strong></div>
              </div>
              <div className={styles.productSectionTitle}>{comparableOffers ? "Compare providers" : `Choose ${optionKind}`}</div>
              <div className={styles.offerCards}>
                {selectedItem.offers?.map((offer, index) => (
                  <button type="button" className={`${styles.offerCard} ${index === selectedOfferIndex ? styles.offerCardActive : ""}`} key={`${offer.provider}-${index}`} onClick={() => setSelectedOfferIndex(index)}>
                    <span className={styles.offerRadio} />
                    <span className={styles.offerName}>
                      <strong>{offer.provider}{comparableOffers && index === 0 && <em className={styles.bestBadge}><i />Best rate</em>}</strong>
                      <small>{offer.network}</small>
                    </span>
                    <span className={styles.offerRate}><strong>{offer.rate}</strong><small>{offer.terms}</small></span>
                  </button>
                ))}
              </div>
              <div className={styles.productSectionTitle}>How it works</div>
              <div className={styles.flowSteps}>
                <div><b>1</b><span><strong>Select</strong><small>Choose the {optionKind} and terms</small></span></div>
                <div><b>2</b><span><strong>Review</strong><small>Enter amount, fees and minimum received</small></span></div>
                <div><b>3</b><span><strong>Sign</strong><small>Confirm in Alfa Wallet</small></span></div>
              </div>
              <details className={styles.riskPanel}>
                <summary>Risks and withdrawal terms</summary>
                <p>{selectedItem.risks}</p>
                <div><span>Withdrawal</span><strong>{selectedItem.lock}</strong></div>
                <div><span>Data source</span><strong>{selectedItem.source}</strong></div>
              </details>
            </div>
            <div className={styles.productFooter}>
              <button type="button" disabled>{actionLabel} with Alfa Wallet</button>
              <span>Wallet signing is not enabled yet</span>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default ProviderRows;
